import { ReactNode } from 'react';
import {
  Apex,
  ApexTrigger,
  Assign,
  At,
  Automations,
  Clock,
  Copy,
  Cube,
  Fields,
  Flows,
  Layout,
  PlaybookAlert,
  RecordType,
  Reporting,
  Shuffle,
  Stamp,
  Target,
  ValidationRules,
  Wrench,
} from '@sweep-io/sweep-design/dist/icons';
import { colors } from '@sweep-io/sweep-design';
import { ObjectWithPills } from '../types';
import { ConfigurationItem } from '../../parser/ParserTypes';
import {
  VALIDATION_RULE,
  WORKFLOW_RULE,
  FLOW,
  PROCESS_BUILDER_FLOW,
  APPROVAL_PROCESS,
  APEX_TRIGGER,
  APEX_CLASS,
  REPORT,
  LAYOUT,
  FIELD,
  OBJECT,
  EMAIL_TEMPLATE,
  RECORD_TYPE,
} from '@server/parser';
import {
  SWEEP_AUTOMATION,
  SWEEP_ALERT,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  ROLLUP,
} from '../../parser/ConfigurationItemTypes';

export enum DependenciesContentViewType {
  default = 'default',
  comments = 'comments',
  sourceCode = 'sourceCode',
  editTag = 'editTag',
}

export const SWEEP_AUTOMATION_TEMPLATE_TYPES = [
  SWEEP_AUTOMATION,
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
];

export const SWEEP_DEDUP_MATCHING_TEMPLATE_TYPES = [SWEEP_DEDUPE, SWEEP_MATCHING];

export const ConfigurationToName: {
  [key: string]: string;
} = {
  [VALIDATION_RULE]: 'Validation Rule',
  [WORKFLOW_RULE]: 'Workflow Rule',
  [FLOW]: 'Flow',
  [PROCESS_BUILDER_FLOW]: 'Process Builder',
  [APPROVAL_PROCESS]: 'Approval Process',
  [APEX_TRIGGER]: 'Apex Trigger',
  [APEX_CLASS]: 'Apex Class',
  [REPORT]: 'Report',
  [LAYOUT]: 'Layout',
  [FIELD]: 'Field',
  [OBJECT]: 'Object',
  [EMAIL_TEMPLATE]: 'Email Template',
  [SWEEP_AUTOMATION]: 'Sweep Automation',
  [SWEEP_ALERT]: 'Sweep Alert',
  [SWEEP_DEDUPE]: 'Sweep Deduplication',
  [SWEEP_MATCHING]: 'Sweep Matching',
  [SWEEP_ASSIGNMENT]: 'Sweep Assignment',
  [SWEEP_SCHEDULED_ASSIGNMENT]: 'Sweep Scheduled Assignment',
  [ROLLUP]: 'Rollup',
  [RECORD_TYPE]: 'Record Type',
  standardFields: 'Standard Fields',
  customFields: 'Custom Fields',
};
const iconColor = colors.grey[700];

export const configurationTypeToIcon: {
  [key: string]: ReactNode;
} = {
  [FIELD]: <Fields color={iconColor} size="medium" />,
  [APEX_TRIGGER]: <ApexTrigger color={iconColor} size="medium" />,
  [APEX_CLASS]: <Apex color={iconColor} size="medium" />,
  [VALIDATION_RULE]: <ValidationRules color={iconColor} size="medium" />,
  [WORKFLOW_RULE]: <Shuffle color={iconColor} size="medium" />,
  [FLOW]: <Flows color={iconColor} size="medium" />,
  [PROCESS_BUILDER_FLOW]: <Wrench color={iconColor} size="medium" />,
  [APPROVAL_PROCESS]: <Stamp color={iconColor} size="medium" />,
  [REPORT]: <Reporting color={iconColor} size="medium" />,
  [LAYOUT]: <Layout color={iconColor} size="medium" />,
  [OBJECT]: <Cube color={iconColor} size="medium" />,
  [EMAIL_TEMPLATE]: <At color={iconColor} size="medium" />,
  [SWEEP_AUTOMATION]: <Automations color={iconColor} size="medium" />,
  [SWEEP_ALERT]: <PlaybookAlert color={iconColor} size="medium" />,
  [SWEEP_DEDUPE]: <Copy color={iconColor} size="medium" />,
  [SWEEP_MATCHING]: <Target color={iconColor} size="medium" />,
  [SWEEP_ASSIGNMENT]: <Assign color={iconColor} size="medium" />,
  [SWEEP_SCHEDULED_ASSIGNMENT]: <Clock color={iconColor} size="medium" />,
  [ROLLUP]: <Fields color={iconColor} size="medium" />,
  [RECORD_TYPE]: <RecordType color={iconColor} size="medium" />,
  standardFields: <Fields color={iconColor} size="medium" />,
  customFields: <Fields color={iconColor} size="medium" />,
};

export type SelectedDependencyWithClearHistoryProps = SelectedDependency & {
  clearHistory?: boolean;
};

export type ConfigurationTypesWithIcon =
  | typeof SWEEP_ALERT
  | typeof SWEEP_AUTOMATION
  | typeof SWEEP_ASSIGNMENT
  | typeof SWEEP_SCHEDULED_ASSIGNMENT
  | typeof SWEEP_DEDUPE
  | typeof SWEEP_MATCHING;

export enum UnclassifiedTypes {
  tag = 'tag',
}

export interface SelectedDependency {
  id: string;
  name: string;
  dependencyType?: string;
  parentType: string;
  objectName?: string;
  contentType: DependenciesContentViewType;
  metadataId?: string;
  label?: string;
}

export interface ObjectWithPillsMap {
  favorites: ObjectWithPills[];
  standardObjects: ObjectWithPills[];
  cpqObjects: ObjectWithPills[];
  customObjects: ObjectWithPills[];
  platformEvents: ObjectWithPills[];
}

export interface LayoutsByObjectName {
  [objectName: string]: { isLoading: boolean; layouts: ConfigurationItem[] };
}
